












































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { ClientProducts, ClientProductsValue } from "@/data/models/ClientAdministrationRow";
import { setClientProducts } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  clientProductsErrorKey,
  derivedProductChoice,
  PRODUCT_SWITCHES,
  productChoiceLabelKey,
  productsDiffer,
  toProductsValue,
} from "@/utils/ClientProducts";

/** What `show()` resolves with: the saved products, CHANGED_MEANWHILE (the caller reloads), or null. */
export type ProductsDialogResult = ClientProducts | "CHANGED_MEANWHILE" | null;

const EMPTY_PRODUCTS: ClientProducts = {
  officeDeviceAssignment: false,
  project: false,
  work: false,
  interface: false,
  derived: false,
};

/**
 * Edits a client's products (#2610089 stage 1): one switch per product (GZ / GL in IRES Office, IRES
 * Project, IRES Work, Schnittstelle). The switches are sent with the ones loaded, so IRES Cloud can refuse
 * a change made meanwhile; it derives the old product choice and recalculates the licences on save.
 *
 * `show()` resolves with the saved products, `CHANGED_MEANWHILE` when IRES Cloud refused because the client
 * changed since (the caller reloads), or null when closed without saving.
 */
@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class ClientProductsDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  readonly PRODUCT_SWITCHES = PRODUCT_SWITCHES;

  busy = false;
  clientId = "";
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  stored: ClientProducts = { ...EMPTY_PRODUCTS };
  form: ClientProductsValue = toProductsValue(EMPTY_PRODUCTS);

  private resolve: ((result: ProductsDialogResult) => void) | null = null;

  get hasChanged(): boolean {
    return productsDiffer(toProductsValue(this.stored), this.form);
  }

  /** The "Produktwahl (alt)" the current switches derive, shown live. */
  get resultingChoiceLabelKey(): string {
    return productChoiceLabelKey(derivedProductChoice(this.form)) ?? "admin.clients.products.choices.DEVICES_ONLY_WEBSITE";
  }

  /**
   * @param clientId - the client.
   * @param stored - its products as the detail loaded them.
   */
  show(clientId: string, stored: ClientProducts): Promise<ProductsDialogResult> {
    this.clientId = clientId;
    this.stored = { ...stored };
    this.form = toProductsValue(stored);
    this.baseDialog.show({
      title: this.$t("admin.clients.products.editTitle").toString(),
      positiveButtonText: this.$t("common.save").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 560,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onSave() {
    if (!this.hasChanged) {
      return;
    }
    this.busy = true;
    try {
      const saved = await setClientProducts(this.clientId, toProductsValue(this.stored), { ...this.form });
      SnackbarModule.showSuccess({ key: "admin.clients.products.saved" });
      this.close(saved ?? "CHANGED_MEANWHILE");
    } catch (error) {
      const key = clientProductsErrorKey(error);
      SnackbarModule.showError({ key });
      // Someone changed the client meanwhile: close, so the caller reloads from the new values.
      if (key.endsWith("CHANGED_MEANWHILE")) {
        this.close("CHANGED_MEANWHILE");
      }
    } finally {
      this.busy = false;
    }
  }

  close(result: ProductsDialogResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: ProductsDialogResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
